import { h } from '@stencil/core';

// src/lib/app-utils.ts
function getConfig(packageIdentifier) {
  if (!window.sydleApp?.config?.[packageIdentifier]) {
    return null;
  }
  return window.sydleApp?.config?.[packageIdentifier];
}
function createTranslator(bundleIdentifier) {
  return (key, defaultValue, language) => {
    const bundle = window?.sydleApp?.translationBundles?.[bundleIdentifier];
    const currentLanguage = language ?? window.sydleApp?.api?.["userAPI"]?.getCurrentLanguage();
    if (!currentLanguage || !bundle) {
      return defaultValue;
    }
    return bundle?.[key]?.[currentLanguage] ?? defaultValue;
  };
}

// src/lib/context-utils.ts
function createConsumer(element, contextKey, timeout = 4e3) {
  const targetElement = element;
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      console.error(`Provider not found: '${contextKey}'`);
      reject();
    }, timeout);
    targetElement?.dispatchEvent(
      new CustomEvent("syContextConsumerDidSubscribe", {
        bubbles: true,
        composed: true,
        detail: {
          context: contextKey,
          el: targetElement,
          changeValue: (newValue) => {
            targetElement[contextKey] = newValue;
            clearTimeout(timeoutId);
            resolve?.(newValue);
          }
        }
      })
    );
  });
}
function createProvider(element, contextKey, value) {
  const targetElement = element;
  targetElement.__provides = targetElement.__provides ?? {};
  const oldProvider = targetElement.__provides[contextKey];
  targetElement.__provides[contextKey] = {
    value,
    consumers: oldProvider ? oldProvider.consumers : /* @__PURE__ */ new Set()
  };
  oldProvider?.consumers?.forEach((consumer) => {
    consumer.changeValue(value);
  });
  if (targetElement.__wrapped)
    return;
  targetElement.__wrapped = true;
  const subscribeConsumerEventListener = (event) => {
    const { context, changeValue, el } = event.detail;
    if (!targetElement.__provides) {
      return;
    }
    if (!(context in targetElement.__provides)) {
      return;
    }
    const consumer = { el, changeValue };
    const currentProvider = targetElement.__provides[context];
    if (currentProvider) {
      currentProvider.consumers.add(consumer);
      consumer.changeValue(currentProvider.value);
      const previousDisconnectConsumer = consumer.el.disconnectConsumer;
      el.disconnectConsumer = () => {
        previousDisconnectConsumer?.();
        currentProvider.consumers.delete(consumer);
      };
      event.stopPropagation();
    }
  };
  targetElement.updateProvider = (contextKey2, value2) => {
    if (!targetElement.__provides) {
      return;
    }
    const currentProvider = targetElement.__provides[contextKey2];
    if (!currentProvider) {
      console.warn("Provider not found. Use CreateProvider before.");
      return;
    }
    currentProvider.value = value2;
    currentProvider.consumers?.forEach((el) => {
      el.changeValue(value2);
    });
  };
  targetElement.disconnectProvider = () => {
    targetElement.__provides = void 0;
    targetElement.__wrapped = void 0;
    targetElement.updateProvider = void 0;
    targetElement.removeEventListener("syContextConsumerDidSubscribe", subscribeConsumerEventListener);
    targetElement.disconnectProvider = void 0;
  };
  targetElement.addEventListener("syContextConsumerDidSubscribe", subscribeConsumerEventListener);
}

// src/lib/debounce-decorator.ts
var Debounce = (duration, keyFn) => {
  return (_target, key, descriptor) => {
    const originalMethod = descriptor.value;
    if (keyFn && keyFn.length > originalMethod.length) {
      console.error(`Debounce decorator keyFn argument list must match the decorated function (${key}). `);
    }
    descriptor.value = function(...args) {
      this.__debounce = this.__debounce ?? {};
      const debounceKey = keyFn ? `${key}.${keyFn(...args)}` : key;
      clearTimeout(this.__debounce[debounceKey]);
      this.__debounce[debounceKey] = setTimeout(() => {
        originalMethod.call(this, ...args);
      }, duration);
    };
  };
};

// src/lib/throttle-decorator.ts
function Throttle(delay) {
  return function(_target, key, descriptor) {
    const originalMethod = descriptor.value;
    descriptor.value = function(...args) {
      this.__shouldWait = this.__shouldWait ?? {};
      this.__waitingArgs = this.__waitingArgs ?? {};
      const setTimeoutFn = () => {
        if (this.__waitingArgs?.[key]) {
          originalMethod.call(this, ...this.__waitingArgs[key]);
          this.__waitingArgs[key] = null;
          setTimeout(setTimeoutFn, delay);
        } else {
          if (!this.__shouldWait) {
            this.__shouldWait = {};
          }
          this.__shouldWait[key] = false;
        }
      };
      if (this.__shouldWait[key]) {
        this.__waitingArgs[key] = args;
        return;
      }
      this.__shouldWait[key] = true;
      originalMethod.call(this, ...args);
      setTimeout(setTimeoutFn, delay);
    };
  };
}
function renderComponent(componentContent) {
  return h(componentContent.tag, componentContent.node, parseChildren(componentContent.children));
}
function parseChildren(children) {
  if (children.length < 1) {
    return [null];
  }
  return children.map((child) => {
    if (typeof child === "string") {
      return child;
    }
    return h(child.tag, child.node, parseChildren(child.children));
  });
}
function reloadPageContent(element) {
  const reloadEvent = new CustomEvent("syAppCustomPageReloadElements", { bubbles: true });
  if (element) {
    return element.dispatchEvent(reloadEvent);
  }
  return document.body.dispatchEvent(reloadEvent);
}

// src/lib/router-utils.ts
function getRouteByIdentifier(identifier) {
  return getConfig("ui-app")?.routes.find((route) => route.name === identifier);
}
function getRouteById(id, routes) {
  routes = routes ?? getConfig("ui-app")?.routes;
  for (const route of routes || []) {
    if (route._id === id) {
      return route;
    }
    if (route.children) {
      const foundRoute = getRouteById(id, route.children);
      if (foundRoute) {
        return foundRoute;
      }
    }
  }
  return null;
}
var getAbsoluteUrl = function() {
  let a;
  return function(url = "") {
    if (!a)
      a = document.createElement("a");
    a.href = url;
    return a.href;
  };
}();
function getRouter() {
  if (!window.sydleApp?.router?.instance) {
    return null;
  }
  return window.sydleApp.router.instance;
}
function getAction(routeIdentifier) {
  if (!routeIdentifier || !window?.sydleApp?.router?.actions) {
    return null;
  }
  return window.sydleApp.router.actions[routeIdentifier] ?? null;
}
function setAction(routeIdentifier, actionFunction) {
  if (!window.sydleApp) {
    window.sydleApp = {};
  }
  if (!window.sydleApp?.router) {
    window.sydleApp.router = {};
  }
  if (!window.sydleApp.router.actions) {
    window.sydleApp.router.actions = {};
  }
  let previousAction;
  if (window.sydleApp.router.actions[routeIdentifier]) {
    previousAction = window.sydleApp.router.actions[routeIdentifier];
  }
  window.sydleApp.router.actions[routeIdentifier] = async (context, commands) => {
    const previousActionReturnValue = await previousAction?.(context, commands);
    if (previousActionReturnValue) {
      return previousActionReturnValue;
    }
    return actionFunction(context, commands);
  };
}
function setActionAll(actionFunction, options) {
  const routes = getConfig("ui-app")?.routes?.map((route) => route.name)?.filter((route) => route && !options?.except?.includes(route));
  routes?.forEach((route) => route && setAction(route, actionFunction));
}
function urlFor(name, params, router = getRouter()) {
  let path;
  try {
    path = router?.urlForName(name, params) ?? "";
  } catch (error) {
    console.error(error);
    path = router?.urlForName("home") ?? "";
  }
  return removeBaseUrl(path);
}
function navigate(name, params, router = getRouter()) {
  const path = urlFor(name, params, router);
  return navigateToPath(path);
}
function navigateToPath(path) {
  window.history.pushState(null, document.title, path);
  return window.dispatchEvent(new PopStateEvent("popstate"));
}
function removeBaseUrl(path) {
  return path.replace(getRouter()?.baseUrl ?? "", "");
}

// src/lib/seo-utils.ts
function setHTMLTitle(title, sufix = "") {
  const titleElement = document.head.querySelector("title");
  if (titleElement && (title || sufix)) {
    const isFullTitle = title && sufix;
    titleElement.innerHTML = isFullTitle ? `${title} | ${sufix}` : title || sufix;
  }
}
function getHTMLTitle() {
  const titleElement = document.head.querySelector("title");
  return titleElement?.innerHTML;
}

// src/lib/url-utils.ts
function setURLParam(param, value, href = window.location.href) {
  const url = new URL(href);
  url.searchParams.set(param, value);
  window.history.replaceState(null, "", url.href);
  return url.href;
}
function getURLParam(param, href = window.location.href) {
  const url = new URL(href);
  return url.searchParams.get(param);
}
function getRelativeURL(href) {
  const currentLocation = window.location;
  const currentHref = currentLocation.href.endsWith("/") ? currentLocation.href : `${currentLocation.href}/`;
  const url = new URL(href, currentHref);
  return url.pathname;
}
function deleteURLParam(param, href = window.location.href) {
  const url = new URL(href);
  url.searchParams.delete(param);
  window.history.replaceState(null, "", url.href);
  return url.href;
}

export { Debounce, Throttle, createConsumer, createProvider, createTranslator, deleteURLParam, getAbsoluteUrl, getAction, getConfig, getHTMLTitle, getRelativeURL, getRouteById, getRouteByIdentifier, getRouter, getURLParam, navigate, navigateToPath, reloadPageContent, renderComponent, setAction, setActionAll, setHTMLTitle, setURLParam, urlFor };
//# sourceMappingURL=out.js.map
//# sourceMappingURL=index.mjs.map